/**
 * Preserving the intended path across the Entra round trip.
 *
 * Master spec §8e and Phase 1 step 7: a user clicking a deep link from the
 * action digest must land on THAT opportunity's Actions tab after signing in,
 * not on the dashboard.
 *
 * This is small and it decides whether anyone trusts the reminder email. The
 * digest deliberately carries no customer names — only `opp_number` and a link
 * — so a link that dumps the user on the dashboard leaves them with nothing to
 * act on, and they stop opening the mail.
 */

const STATE_VERSION = 'v1';

/**
 * MSAL round-trips `state` through Entra. It is echoed back verbatim, so it
 * must not be trusted as a URL: an attacker who can start a sign-in could put
 * anything in it, and navigating to an arbitrary value is an open redirect.
 * Hence `sanitizeTarget`.
 */
export function encodeTarget(pathWithQuery: string): string {
  return `${STATE_VERSION}:${encodeURIComponent(pathWithQuery)}`;
}

export function decodeTarget(state: string | null | undefined): string | null {
  if (!state || !state.startsWith(`${STATE_VERSION}:`)) return null;
  try {
    return sanitizeTarget(decodeURIComponent(state.slice(STATE_VERSION.length + 1)));
  } catch {
    return null;
  }
}

/**
 * Accept only a same-origin, path-absolute target.
 *
 * Rejected: anything with a scheme, a protocol-relative `//host` prefix, or a
 * backslash (which some browsers normalize to `/`, turning `/\evil.com` into a
 * cross-origin navigation).
 */
export function sanitizeTarget(target: string): string | null {
  if (typeof target !== 'string' || target.length === 0) return null;
  if (!target.startsWith('/')) return null;
  if (target.startsWith('//')) return null;
  if (target.includes('\\')) return null;
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(target)) return null;
  return target;
}

/** The path the user is on now, including query and hash, for stashing in `state`. */
export function currentTarget(location: Location = window.location): string {
  const target = `${location.pathname}${location.search}${location.hash}`;
  // The bare root is not worth restoring, and restoring it would fight the
  // post-login redirect.
  return target === '/' ? '/' : target;
}
